import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import React, { useState } from "react";

const TermsAndConditionsModal = ({ open, onClose, onAccept }) => {
  const [checked, setChecked] = useState(false);

  const handleCheckboxChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleAccept = () => {
    if (checked) {
      onAccept();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{
        color: "text.secondary",
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography>Terms and Conditions</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography>Effective Date: November 1, 2024</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText>
          <Typography>
            Welcome to CarChaser.ca, a cutting-edge online platform connecting
            private sellers and licensed auto dealers through a 95-minute live
            auction. By accessing or using our services, you acknowledge and
            agree to be legally bound by these Terms and Conditions. If you do
            not accept these terms, you must refrain from using our platform.
          </Typography>

          <Typography>1. Online Terms</Typography>
          <Typography>
            By agreeing to these Terms, you enter into a legally binding
            consignment agreement with CarChaser.ca, designating us as your
            agent to facilitate the sale of your vehicle through our platform.
          </Typography>

          <Typography>2. General Conditions</Typography>
          <Typography>
            - You agree to provide accurate, complete, and truthful information
            about your vehicle.
            <br />
            - CarChaser.ca reserves the right to refuse service at our sole
            discretion.
            <br />- You must be at least 18 years old to use our platform and
            comply with all applicable laws.
          </Typography>

          <Typography>3. Vehicle Information and Seller Obligations</Typography>
          <Typography>
            You are solely responsible for the accuracy and completeness of all
            information provided, including but not limited to:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            - Vehicle condition (mechanical, electrical, or cosmetic issues).
            <br />- Mileage, ownership history, and accident records.
          </Typography>
          <Typography>
            Failure to disclose material facts may result in arbitration,
            financial penalties, or reversal of the transaction.
          </Typography>
          <Typography>You further represent and warrant that you:</Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            - Hold lawful ownership of the vehicle or have legal authorization
            to sell it.
            <br />- Acknowledge that failure to comply with these Terms may
            result in CarChaser.ca reversing the transaction, returning the
            vehicle, and recovering any exchanged funds.
          </Typography>

          <Typography>4. Fees and Pricing</Typography>
          <Typography>
            - Sellers must pay a{" "}
            <strong>
              non-refundable upfront fee of $65 for a CARFAX report
            </strong>
            .<br />
            - CarChaser.ca charges a flat fee based on the vehicle's value, plus
            applicable HST.
            <br />- Fees and charges are subject to change with reasonable
            notice.
          </Typography>

          <Typography>5. Auction Terms</Typography>
          <Typography>
            CarChaser.ca acts solely as a facilitator. By using our platform:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            - If your vehicle's reserve price is met, the sale is final and
            binding.
            <br />- Dealers' bids are irrevocable and binding once placed.
          </Typography>

          <Typography>6. Third-Party Tools and Links</Typography>
          <Typography>
            The platform may provide access to third-party tools or links,
            offered "as is" without warranties. <strong>CarChaser.ca</strong>{" "}
            assumes no liability for third-party content or services.
          </Typography>

          <Typography>7. Personal Information</Typography>
          <Typography>
            The handling of your personal information is governed by our{" "}
            <Link href="#privacy-policy">Privacy Policy</Link>.
          </Typography>

          <Typography>
            8. Disclaimer of Warranties and Limitation of Liability
          </Typography>
          <Typography>
            CarChaser.ca provides services "as is" without guarantees of
            uninterrupted access, error-free performance, or specific outcomes.
          </Typography>

          <Typography>9. Indemnification</Typography>
          <Typography>
            You agree to indemnify and hold CarChaser.ca harmless from claims,
            damages, or liabilities arising from:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            - Misrepresentation or failure to disclose material facts about your
            vehicle.
            <br />- Breaches of these Terms.
          </Typography>

          <Typography>10. Arbitration Policy</Typography>
          <Typography>
            Disputes arising from transactions, including undisclosed issues,
            must be submitted within 5 business days. CarChaser.ca will mediate
            the dispute, and the resolution may include compensation or vehicle
            return.
          </Typography>

          <Typography>11. Governing Law</Typography>
          <Typography>
            These Terms are governed by the laws of Ontario, Canada. All
            disputes will be resolved under Ontario's jurisdiction.
          </Typography>

          <Typography id="privacy-policy" sx={{ mt: 4 }}>
            Privacy Policy
          </Typography>
          <Typography>Effective Date: November 1, 2024</Typography>
          <Typography>
            At CarChaser.ca, we are committed to safeguarding your privacy. This
            Privacy Policy explains how we collect, use, and protect your
            personal information.
          </Typography>

          <Typography>1. Information We Collect</Typography>
          <Typography>1.1 Personal Information:</Typography>
          <Typography>
            Name, contact details, payment information, and dealer license
            information for account setup and transactions.
          </Typography>
          <Typography>1.2 Vehicle Information:</Typography>
          <Typography>
            VIN, make, model, year, mileage, and condition for auction purposes.
          </Typography>
          <Typography>1.3 Usage Data:</Typography>
          <Typography>
            Information such as IP address and browser details collected during
            your interaction with our platform.
          </Typography>

          <Typography>2. How We Use Your Information</Typography>
          <Typography>We use your information to:</Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            - Facilitate vehicle auctions and transactions.
            <br />
            - Improve platform functionality and resolve technical issues.
            <br />- Comply with legal obligations.
          </Typography>

          <Typography>3. Sharing Information</Typography>
          <Typography>Your information may be shared with:</Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            - Service Providers: For payment processing and support.
            <br />
            - Dealers: To enable auction-related communication.
            <br />- Legal Authorities: As required by law.
          </Typography>
          <Typography>
            We do not sell your personal information to third parties.
          </Typography>

          <Typography>4. Data Security</Typography>
          <Typography>
            We implement robust measures to protect your personal data; however,
            no system is entirely secure, and you acknowledge potential risks.
          </Typography>

          <Typography>5. Cookies</Typography>
          <Typography>
            We use cookies to improve user experience and monitor platform
            performance. You can manage cookie preferences through your browser
            settings.
          </Typography>

          <Typography>6. User Rights</Typography>
          <Typography>You have the right to:</Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            - Access and update your personal information.
            <br />
            - Request the deletion of your data, subject to legal obligations.
            <br />- Opt out of marketing communications at any time.
          </Typography>

          <Typography>7. Policy Updates</Typography>
          <Typography>
            This Privacy Policy may be updated periodically. Changes will take
            effect upon posting to our platform.
          </Typography>

          <Typography>8. Contact Us</Typography>
          <Typography>
            For questions or concerns, please reach out to us at:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            Email:{" "}
            <Link href="mailto:contact@carchaser.ca">contact@carchaser.ca</Link>
            <br />
            Phone: <Link href="tel:6476896208">647-689-6208</Link>
            <br />
            Address: 801-1275 Finch Ave W, ON Toronto M3J 0L5
          </Typography>

          {/* <FormControlLabel
            control={
              <Checkbox
                sx={{ color: "primary.main", alignItems: "center" }}
                checked={checked}
                onChange={handleCheckboxChange}
                color="primary"
                required
              />
            }
            label={
              "I have read, understood, and agree to be bound by these Terms and Conditions and Privacy Policy."
            }
            sx={{ mt: 3 }}
          /> */}
        </DialogContentText>
      </DialogContent>
      {/* <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleAccept} color="primary" disabled={!checked}>
          Accept Terms
        </Button>
      </DialogActions> */}
    </Dialog>
  );
};

export default TermsAndConditionsModal;
